/// useWorkerEvent: React hook for subscribing to specific worker events.
/// Provides typed event handlers that automatically subscribe/unsubscribe
/// with proper React lifecycle management.

import { useEffect, useRef } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import type { MessageEnvelope } from "@lib/MessageEnvelope.js";

/**
 * Subscribe to all worker events.
 * The handler is called for every event from any registered worker.
 *
 * @param handler - Callback receiving (workerId, eventType, payload)
 * @param enabled - Whether the subscription is active (default: true)
 */
export function useAllWorkerEvents(
  handler: (workerId: string, eventType: string, payload: Record<string, unknown>) => void,
  enabled = true,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const manager = getWorkerManager();
    const unsubscribe = manager.onEvent((workerId: string, env: MessageEnvelope) => {
      handlerRef.current(workerId, env.type, env.payload);
    });

    return unsubscribe;
  }, [enabled]);
}
